import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CheckoutFlow from '../components/CheckoutFlow';
import StripePayment from '../components/StripePayment';
import { useCart } from '../context/CartContext';

function CheckoutDemo() {
  const navigate = useNavigate();
  const { items, getCartTotal, clearCart } = useCart();
  const [showCheckoutFlow, setShowCheckoutFlow] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const handleCheckoutComplete = (orderData) => {
    console.log('Checkout completed with order data:', orderData);
    setOrderData(orderData);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (paymentResult) => {
    console.log('Payment successful:', paymentResult);
    // Clear cart after successful payment
    clearCart();
    // Navigate to success page or show success message
    navigate('/payment-success', { 
      state: { 
        orderData: orderData,
        paymentResult: paymentResult 
      }
    });
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    // Handle payment error
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setOrderData(null);
  };

  if (showPayment && orderData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment</h1>
            <p className="text-gray-600">Complete your order with secure payment</p>
          </div>
          
          <StripePayment
            orderData={orderData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onCancel={handlePaymentCancel}
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (showCheckoutFlow) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutFlow
            onComplete={handleCheckoutComplete}
            onCancel={() => setShowCheckoutFlow(false)}
          />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Checkout Flow Demo</h1>
          <p className="text-gray-600">Experience the new checkout flow with guest checkout and step visualization</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Features</h2>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-ambre-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Step-by-step checkout visualization</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-ambre-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Guest checkout option (email + address only)</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-ambre-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Account creation during checkout</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-ambre-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Form validation and error handling</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-ambre-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Responsive design for all devices</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Cart</h2>
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Your cart is empty</p>
                  <button
                    onClick={() => navigate('/products')}
                    className="px-6 py-2 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 transition-colors"
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total:</span>
                      <span>€{getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => setShowCheckoutFlow(true)}
              disabled={items.length === 0}
              className="px-8 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              {items.length === 0 ? 'Add items to cart first' : 'Start Checkout Flow'}
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default CheckoutDemo; 